import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import pipaanlogo from "../../assets/pipaanlogo.png";
import { ACCENT } from "../artistStudio/shared";
import { performLogout } from "../../utils/auth";
import { fanHomeApi, isSignedIn, storedFirstName } from "./fanHomeApi";

// The frame of the music pages (Fan Home, Sound Detail), laid out like the design: a left sidebar with Home, My Library,
// My Plan and Following, and a top bar with the person's name. Guests have no library, plan or following list, so they
// get Home and a Log in button instead.
const fullName = () => {
  try {
    const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
    const user = raw ? JSON.parse(raw) : null;
    const name = [user?.first_name || user?.firstName, user?.last_name || user?.lastName].filter(Boolean).join(" ");
    return name || user?.name || user?.username || storedFirstName();
  } catch (e) {
    return storedFirstName();
  }
};

const pill = "flex w-full items-center gap-3 rounded-full px-5 py-2.5 text-left text-[15px] font-semibold transition";
const HomeIcon = () => <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 11.5 12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-8.5Z" /></svg>;
const LibraryIcon = () => <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></svg>;
const PlanIcon = () => <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg>;

function Following() {
  const [state, setState] = useState({ loading: true, items: [], error: false });
  useEffect(() => {
    let live = true;
    fanHomeApi.following()
      .then((d) => live && setState({ loading: false, items: d?.items || [], error: false }))
      .catch(() => live && setState({ loading: false, items: [], error: true }));
    return () => { live = false; };
  }, []);
  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <p className={`${pill} mb-2 bg-white text-gray-900`}>Following</p>
      {state.loading && <p className="px-5 text-sm text-gray-400">Loading…</p>}
      {state.error && <p className="px-5 text-sm text-gray-400">Couldn't load.</p>}
      {!state.loading && !state.error && !state.items.length && <p className="px-5 text-sm text-gray-400">Follow artists to see them here.</p>}
      <ul className="space-y-1">
        {state.items.map((a) => (
          <li key={a.artist_id}>
            <Link to={`/artists/${encodeURIComponent(a.slug)}`} className="block truncate rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-100">{a.name}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// The search box in the top bar. On other pages, clicking it opens the Search page and Enter opens Search with what was
// typed. On the Search page it is the page's own box (`search` carries its text, ref and submit).
function TopSearch({ search }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const controlled = Boolean(search);
  const submit = (e) => {
    e.preventDefault();
    if (controlled) { search.onSubmit(); return; }
    navigate(`/fan/search${text.trim() ? `?q=${encodeURIComponent(text.trim())}` : ""}`);
  };
  return (
    <form role="search" onSubmit={submit} className="relative order-last w-full md:order-none md:w-auto md:flex-1 md:max-w-[410px]">
      <svg viewBox="0 0 24 24" className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
      <input
        ref={controlled ? search.inputRef : undefined} type="search" value={controlled ? search.text : text}
        onChange={(e) => (controlled ? search.setText(e.target.value) : setText(e.target.value))}
        onClick={() => { if (!controlled) navigate("/fan/search"); }}
        maxLength={100} aria-label="Search sounds, artists and playlists"
        placeholder="Search for songs, sounds or artists" data-testid={controlled ? "search-input" : "shell-search"}
        className="w-full rounded-full border border-transparent bg-[#f4f3f1] py-2.5 pl-10 pr-4 text-[13px] outline-none placeholder:text-gray-400 focus:border-gray-900 focus:bg-white"
      />
    </form>
  );
}

export default function MusicShell({ children, maxWidth = "max-w-[1136px]", search }) {
  const signedIn = isSignedIn();
  const name = signedIn ? fullName() : "";
  const nav = ({ isActive }) => `${pill} ${isActive ? "bg-gray-900 text-white" : "bg-white text-gray-900 hover:bg-gray-100"}`;

  return (
    <div className="min-h-screen w-full bg-white md:flex">
      <aside className="hidden md:flex sticky top-0 h-screen w-72 shrink-0 flex-col overflow-y-auto border-r border-gray-200 bg-[#f6f5f3] px-4 py-8" aria-label="Music navigation">
        <Link to="/home" aria-label="Nyawoo home" className="block px-2 pb-8"><img src={pipaanlogo} alt="Nyawoo" className="h-10 w-auto" /></Link>
        <nav className="space-y-3">
          <NavLink to="/fan/home" className={nav}><HomeIcon /> Home</NavLink>
          {signedIn && (
            <>
              <NavLink to="/fan/library" className={nav}><LibraryIcon /> My Library</NavLink>
              <NavLink to="/fan/plan" className={nav}><PlanIcon /> My Plan</NavLink>
            </>
          )}
        </nav>
        {signedIn && <Following />}
        {signedIn && (
          <div className="mt-auto space-y-3 pt-8">
            <Link to="/account" className={`${pill} bg-white text-gray-900 hover:bg-gray-100`}>Settings</Link>
            <Link to="/rights" className={`${pill} bg-white text-gray-900 hover:bg-gray-100`}>Rights &amp; Legal</Link>
            <button type="button" onClick={() => performLogout()} className={`${pill} bg-white text-gray-900 hover:bg-gray-100`}>Logout</button>
          </div>
        )}
      </aside>

      <div className="min-w-0 flex-1">
        <header className="flex flex-wrap items-center gap-3 border-b border-gray-100 md:border-0 px-4 md:px-10 py-4 md:pt-6 md:pb-0">
          <Link to="/home" aria-label="Nyawoo home" className="md:hidden"><img src={pipaanlogo} alt="Nyawoo" className="h-8 w-auto" /></Link>
          <nav className="md:hidden flex items-center gap-3 whitespace-nowrap text-sm font-semibold" style={{ color: ACCENT }}>
            <Link to="/fan/home" className="hover:underline">Home</Link>
            <Link to="/fan/search" className="hover:underline">Search</Link>
            {signedIn && <Link to="/fan/library" className="hover:underline">Library</Link>}
            {signedIn && <Link to="/fan/plan" className="hover:underline">My Plan</Link>}
            <Link to="/rights" className="hover:underline">Rights</Link>
          </nav>
          <TopSearch search={search} />
          <div className="ml-auto flex items-center gap-4 text-sm font-semibold">
            {signedIn
              ? <span className="text-gray-900 md:hidden" data-testid="shell-user">{name}</span>
              : <Link to="/login" className="rounded-full text-white px-4 py-1.5" style={{ backgroundColor: ACCENT }}>Log in</Link>}
          </div>
        </header>
        <main className={`${maxWidth} w-full px-4 md:px-10 pt-8 pb-32`}>{children}</main>
      </div>
    </div>
  );
}
